import React, { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login';
import { useGoogleLogin } from '@react-oauth/google';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import { ButtonClassNames, GlobalRouteNames, InputTextFormat, InputRegexPattern } from '../../../models/Common';
import { Envelope } from '../../commons/icons/Envelope';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { LoginWithSocials } from '../LoginWithSocials';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { FormInputText } from '../../inputs/FormInputText';
import { FormInputOtp } from '../../inputs/FormInputOtp';
import { ApiError } from '../../../models/ApiError';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { ReactComponent as ArrowLinkIcon } from '../../../assets/images/arrows/ArrowsLink.svg';
import { socialsApi } from '../../../services/apiServices/user/socials';
import { AppleLoginResponse, TypeOfSocials } from '../../../models/Socials';
import { SocialsProps } from '../../../services/apiServices/user/authorization';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { useSocialAuthorization } from '../../../hooks/useSocialAuthorization';
import { parseJwt, getErrorMessage } from '../../../utils';
import { googleLoginURL } from '../../../constants/configuration';

interface TwoFactorAuthProps {
  setIsPageToTop: (setIsPageToTop: boolean) => void;
  fromLocation: {
    pathname: string;
    search: string;
  };
}

export const TwoFactorAuth: FC<TwoFactorAuthProps> = ({ setIsPageToTop, fromLocation }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isInput, setIsInput] = useState({ isPhoneInput: false, isCodeInput: false });
  const [isAddingNewPhone, setIsAddingNewPhone] = useState(false);
  const isDesktop = useMediaQuery('(max-width:767px)');
  const [connectSocialAPI] = socialsApi.useConnectMutation();
  const { socialAuth } = useSocialAuthorization(fromLocation);
  const { authData } = useAppSelector((state) => state.authReducer);
  const { setCredentials } = authSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { data: userInfo, isLoading: isLoadingUserInfo } = userInfoApi.useGetUserInfoQuery('', {
    skip: !authData.accessToken && !authData.refreshToken,
  });
  const [requestConfirmationSmsCodeApi, { isLoading: isLoadingRequestCode }] =
    userInfoApi.useRequestConfirmationSmsCodeMutation();
  const [checkConfirmationSmsCodeApi, { isLoading: isLoadingCheckCode }] =
    userInfoApi.useCheckConfirmationSmsCodeMutation();
  const [addNewSmsPhoneApi, { isLoading: isLoadingAddPhone }] = userInfoApi.useAddNewSmsPhoneMutation();

  const userPhone = userInfo?.SmsSettings?.Phone || '';
  const encodedPhone = `+38${userPhone.slice(0, 1)}${userPhone.slice(1, 6).replace(/[0-9]/gi, '.')}${userPhone.slice(
    6,
  )}`;
  const isFacebook = userInfo?.SocialAccounts?.some((social) => social.Network === TypeOfSocials.Facebook);
  const isGoogle = userInfo?.SocialAccounts?.some((social) => social.Network === TypeOfSocials.Google);
  const isApple = userInfo?.SocialAccounts?.some((social) => social.Network === TypeOfSocials.Apple);

  const methods = useForm({
    defaultValues: { phone: '', smsCode: '' },
  });
  const {
    setError,
    formState: { errors },
    handleSubmit,
    setValue,
  } = methods;

  const handleHome = () => {
    history.push(GlobalRouteNames.HomePage);
  };

  const handleChangePhone = () => {
    setIsInput({ isCodeInput: false, isPhoneInput: true });
    setIsAddingNewPhone(true);
    setValue('phone', '');
  };

  const addNewPhone = async (phone: string) => {
    try {
      await addNewSmsPhoneApi({ phone })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.mainPhoneNumberAddedSuccessfully'), 'success');
        });
    } catch (err) {
      const error = err as ApiError;
      setError('smsCode', {
        type: 'server',
        message: error?.data?.error.message,
      });
    }
  };

  const handleRequestCode = async ({ phone }: { phone: string }) => {
    try {
      await requestConfirmationSmsCodeApi({ phone })
        .unwrap()
        .then(() => {
          setIsInput({ isCodeInput: true, isPhoneInput: false });
        });
    } catch (err) {
      const error = err as ApiError;
      if (!isAddingNewPhone) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      } else {
        setError('phone', {
          type: 'server',
          message: error?.data?.error?.message ?? '',
        });
      }
    }
  };

  const handleSubmitCode = async ({ phone, smsCode }: { phone: string; smsCode: number }) => {
    try {
      await checkConfirmationSmsCodeApi({ phone, code: smsCode })
        .unwrap()
        .then(() => {
          if (isAddingNewPhone) {
            addNewPhone(phone);
          }
          dispatch(setCredentials({ accessToken: authData.accessToken, refreshToken: authData.refreshToken }));
          history.replace(`${fromLocation?.pathname}${fromLocation?.search}`);
        });
    } catch (err) {
      const error = err as ApiError;
      setError('smsCode', {
        type: 'server',
        message: error?.data?.error.message ?? '',
      });
    }
  };

  const handleConnectSocial = async (props: SocialsProps) => {
    await connectSocialAPI(props).unwrap();
  };

  const handleAppleLogin = async (res: AppleLoginResponse) => {
    const isOnline = 'authorization' in res;
    const jwt = parseJwt(res.authorization.id_token);
    if (isOnline) {
      if (isApple) {
        await socialAuth({
          type: TypeOfSocials.Apple,
          socialUserId: jwt.sub,
          email: jwt.email,
        });
      } else {
        await handleConnectSocial({
          type: TypeOfSocials.Apple,
          socialUserId: jwt.sub,
          email: jwt.email,
        }).then(() => {
          customEnqueueSnackbar(t('snackbar.appleAccountWasConnected'), 'success');
          dispatch(setCredentials({ accessToken: authData.accessToken, refreshToken: authData.refreshToken }));
          history.push(GlobalRouteNames.HomePage);
        });
      }
    }
  };

  const handleGoogleLoginError = (err: string | undefined) => {
    if (err) {
      customEnqueueSnackbar(err, 'error');
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const info = await fetch(googleLoginURL, {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      const userGoogleInfo = await info.json();
      const isOnline = 'email' in userGoogleInfo;
      if (isOnline) {
        if (isGoogle) {
          await socialAuth({
            type: TypeOfSocials.Google,
            socialUserId: userGoogleInfo.sub,
            email: userGoogleInfo.email,
          });
        } else {
          handleConnectSocial({
            type: TypeOfSocials.Google,
            socialUserId: userGoogleInfo.sub,
            email: userGoogleInfo.email,
          })
            .then(() => {
              customEnqueueSnackbar(t('snackbar.googleAccountWasConnected'), 'success');
              dispatch(
                setCredentials({
                  accessToken: authData.accessToken,
                  refreshToken: authData.refreshToken,
                }),
              );
              history.push(GlobalRouteNames.HomePage);
            })
            .catch((err) => {
              const message = getErrorMessage(err);
              customEnqueueSnackbar(message, 'error');
            });
        }
      }
    },
    onError: (errorResponse) => handleGoogleLoginError(errorResponse.error_description),
  });

  const handleFacebookLogin = async (res: ReactFacebookLoginInfo | ReactFacebookFailureResponse) => {
    const isSuccess = 'userID' in res;
    if (isSuccess) {
      if (isFacebook) {
        await socialAuth({
          type: TypeOfSocials.Facebook,
          socialUserId: res.userID,
          email: res.email || '',
          accessToken: res.accessToken,
        });
      } else {
        handleConnectSocial({
          type: TypeOfSocials.Facebook,
          socialUserId: res.userID,
          email: res.email || '',
          accessToken: res.accessToken,
        })
          .then(() => {
            customEnqueueSnackbar(t('snackbar.facebookAccountWasConnected'), 'success');
            dispatch(setCredentials({ accessToken: authData.accessToken, refreshToken: authData.refreshToken }));
            history.push(GlobalRouteNames.HomePage);
          })
          .catch((err) => {
            const message = getErrorMessage(err);
            customEnqueueSnackbar(message, 'error');
          });
      }
    }
  };

  useEffect(() => {
    setValue('phone', userInfo?.SmsSettings?.Phone || '');
    setIsPageToTop(true);
    if (userInfo && !userInfo.SmsSettings?.Phone) {
      handleChangePhone();
    }
  }, [userInfo]);

  useEffect(() => {
    if (!authData.accessToken && !authData.refreshToken) {
      history.push(GlobalRouteNames.LoginPage);
    }
  }, []);

  return (
    <div className="two-factor-auth">
      <TextButton
        label={t('common.back')}
        handleClick={handleHome}
        className="primary"
        beforeIcon={<ArrowLinkIcon />}
      />
      <div className="two-factor-auth__content-container">
        <div className="two-factor-auth__content-container__picture-container">
          <Envelope isSmall={!isDesktop} />
        </div>
        <div className="two-factor-auth__content-container__content">
          <h3 className="bold">{t('auth.authorization')}</h3>
          <FormProvider {...methods}>
            <form
              className="two-factor-auth__content-container__content__form"
              onSubmit={isInput.isCodeInput ? handleSubmit(handleSubmitCode) : handleSubmit(handleRequestCode)}
            >
              <p
                className={`two-factor-auth__content-container__content__form__description ${
                  isLoadingUserInfo ? 'skeleton' : 'skeleton--disabled'
                }`}
              >
                {isInput.isCodeInput ? t('auth.enterVerificationCode') : t('auth.anSmsMessageWillBeSent')}
              </p>
              {!isInput.isPhoneInput && !isInput.isCodeInput && (
                <>
                  <div
                    className={`two-factor-auth__content-container__content__form__phone-container ${
                      isLoadingUserInfo ? 'skeleton' : 'skeleton--disabled'
                    }`}
                  >
                    <span className="two-factor-auth__content-container__content__form__phone-container__text">
                      {t('auth.sendCodeToPhoneNumber')}:
                    </span>
                    <span className="two-factor-auth__content-container__content__form__phone-container__phone">
                      {userInfo && encodedPhone}
                    </span>
                    <TextButton
                      label={t('buttons.change')}
                      handleClick={handleChangePhone}
                      className={TextButtonClassNames.PrimaryActive}
                    />
                  </div>
                </>
              )}
              {isInput.isPhoneInput && (
                <div className="two-factor-auth__content-container__content__form__input-container">
                  <FormInputText
                    name="phone"
                    className="primary"
                    label={t('input.phone')}
                    isRequired
                    minLength={10}
                    format={InputTextFormat.Phone}
                    pattern={InputRegexPattern.Phone}
                  />
                </div>
              )}
              {isInput.isCodeInput && (
                <div className="two-factor-auth__content-container__content__form__input-container">
                  <FormInputOtp
                    name="smsCode"
                    inputClassName="primary"
                    containerClassName="login-enter-code-sms-container__form__otp"
                    isRequired
                    minMaxLength={4}
                  />
                  {errors.smsCode?.message && (
                    <p className="login-enter-code-sms-container__form__error">{errors.smsCode?.message}</p>
                  )}
                </div>
              )}
              {!isInput.isCodeInput ? (
                <CustomMUIButton
                  label={t('buttons.sendCode')}
                  className={ButtonClassNames.PrimaryFulfilled}
                  isBold
                  isLoading={isLoadingRequestCode || isLoadingUserInfo}
                  type="submit"
                />
              ) : (
                <CustomMUIButton
                  label={t('auth.logIn')}
                  className={ButtonClassNames.PrimaryFulfilled}
                  isBold
                  type="submit"
                  isLoading={isLoadingCheckCode || isLoadingAddPhone}
                />
              )}
            </form>
          </FormProvider>
          <div className="two-factor-auth__content-container__content__socials">
            <p className="two-factor-auth__content-container__content__socials__description">
              {t('auth.loginWithSocials')}
            </p>
            <LoginWithSocials
              isFacebookAvailable={false}
              isAppleAvailable
              isGoogleAvailable
              handleGoogleLogin={handleGoogleLogin}
              handleFacebookLogin={handleFacebookLogin}
              handleAppleLogin={handleAppleLogin}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
